import React from 'react';

const AgentStatusPanel = ({ agentData, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'busy':
        return 'bg-yellow-500';
      case 'offline':
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">AI Agent Status</h3>
        <span className="text-sm text-gray-500">
          {agentData?.timestamp ? new Date(agentData.timestamp).toLocaleTimeString() : 'Loading...'}
        </span>
      </div>

      <div className="space-y-4">
        {/* Email Agent */}
        <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(agentData?.agents?.email_agent?.status)}`}></div>
              <span className="font-semibold text-gray-900">Email Agent</span>
            </div>
            <span className="text-xs text-gray-500 capitalize">
              {agentData?.agents?.email_agent?.status || 'Unknown'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {agentData?.agents?.email_agent?.sent_today || 0}
              </div>
              <div className="text-xs text-gray-600">Sent Today</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">
                {agentData?.agents?.email_agent?.failed_today || 0}
              </div>
              <div className="text-xs text-gray-600">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {agentData?.agents?.email_agent?.success_rate?.toFixed(0) || 0}%
              </div>
              <div className="text-xs text-gray-600">Success</div>
            </div>
          </div>
        </div>

        {/* Call Agent */}
        <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(agentData?.agents?.call_agent?.status)}`}></div>
              <span className="font-semibold text-gray-900">Call Agent (VAPI)</span>
            </div>
            <span className="text-xs text-gray-500 capitalize">
              {agentData?.agents?.call_agent?.status || 'Unknown'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {agentData?.agents?.call_agent?.calls_today || 0}
              </div>
              <div className="text-xs text-gray-600">Calls Today</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {agentData?.agents?.call_agent?.average_duration?.toFixed(0) || 0}s
              </div>
              <div className="text-xs text-gray-600">Avg Duration</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {agentData?.agents?.call_agent?.success_rate?.toFixed(0) || 0}%
              </div>
              <div className="text-xs text-gray-600">Success</div>
            </div>
          </div>
        </div>

        {/* WhatsApp Agent */}
        <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(agentData?.agents?.whatsapp_agent?.status)}`}></div>
              <span className="font-semibold text-gray-900">WhatsApp Agent</span>
            </div>
            <span className="text-xs text-gray-500 capitalize">
              {agentData?.agents?.whatsapp_agent?.status || 'Unknown'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">
                {agentData?.agents?.whatsapp_agent?.messages_today || 0}
              </div>
              <div className="text-xs text-gray-600">Messages</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {agentData?.agents?.whatsapp_agent?.delivery_rate?.toFixed(0) || 0}%
              </div>
              <div className="text-xs text-gray-600">Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {agentData?.agents?.whatsapp_agent?.response_rate?.toFixed(0) || 0}%
              </div>
              <div className="text-xs text-gray-600">Response</div>
            </div>
          </div>
        </div>

        {/* Market Intelligence Agent */}
        <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded-r-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(agentData?.agents?.market_intelligence?.status)}`}></div>
              <span className="font-semibold text-gray-900">Market Intelligence</span>
            </div>
            <span className="text-xs text-gray-500 capitalize">
              {agentData?.agents?.market_intelligence?.status || 'Unknown'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                {agentData?.agents?.market_intelligence?.queries_today || 0}
              </div>
              <div className="text-xs text-gray-600">Queries Today</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600 mt-2">
                Last Update: {agentData?.agents?.market_intelligence?.last_update ?
                  new Date(agentData.agents.market_intelligence.last_update).toLocaleTimeString() :
                  'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentStatusPanel;

